import { ethers } from 'ethers'
import mproSwapRouterAbi from 'config/abi/mproSwapRouter.json'
import type { ExecuteArgs } from './execute/types'
import type { OnChainVenue } from './types'
import {
  AERO_ROUTER,
  CL3_SWAP_ROUTER,
  CL_SWAP_ROUTER,
  NATIVE_SENTINEL,
  UNI_SWAP_ROUTER02,
  UNI_V2_ROUTER
} from './addresses'

/**
 * The MPRO Lab swap router — the contract every swap goes through when the
 * platform's contract configuration names one (`MPROSwapRouter`).
 *
 * The router takes the platform fee from the input and hands the rest to the
 * venue, so the page's job is threefold: quote the venues for the NET amount,
 * show the fee before the wallet opens, and wrap the venue transaction the
 * adapters already build into one `swap()` call. Nothing about routing
 * changes; the router does not route.
 *
 * Without a configured router the page behaves exactly as before — direct to
 * the venues, no fee. That is a degraded state a misconfiguration produces,
 * never a choice a user is offered (scope 16 D8).
 */

export const SWAP_ROUTER_IFACE = new ethers.utils.Interface(mproSwapRouterAbi as unknown as string[])

/** Basis points of the input the router keeps for the platform. */
export function feeFor(amountIn: bigint, feeBps: number): bigint {
  const bps = BigInt(Math.max(0, Math.min(10_000, Math.round(feeBps))))
  return (amountIn * bps) / 10_000n
}

export interface RouterFee {
  feeBps: number
  /** True when this account pays no fee (the platform's own agents, later). */
  exempt: boolean
}

/**
 * The fee the router would take from `account`, read from the contract. A
 * read failure is reported as "no fee" so the page keeps quoting; the dry run
 * before the wallet prompt is what refuses a transaction the fee would break.
 */
export async function readRouterFee(
  provider: ethers.providers.Provider,
  routerAddress: string,
  account?: string | null
): Promise<RouterFee> {
  try {
    const router = new ethers.Contract(routerAddress, SWAP_ROUTER_IFACE, provider)
    const [bps, exempt] = await Promise.all([
      router.feeBps() as Promise<number>,
      account ? (router.isFeeExempt(account) as Promise<boolean>) : Promise.resolve(false)
    ])
    return { feeBps: Number(bps), exempt: Boolean(exempt) }
  } catch {
    return { feeBps: 0, exempt: false }
  }
}

/** Whether the router will call this venue contract at all. */
export async function isRouterTarget(
  provider: ethers.providers.Provider,
  routerAddress: string,
  target: string
): Promise<boolean> {
  try {
    const router = new ethers.Contract(routerAddress, SWAP_ROUTER_IFACE, provider)
    return Boolean(await router.isTarget(target))
  } catch {
    return false
  }
}

/**
 * The contract each on-chain venue's transaction is sent to — which is exactly
 * what the router's allowlist is a list of.
 */
const VENUE_TARGET: Record<OnChainVenue, string> = {
  'aero-v2': AERO_ROUTER,
  'aero-cl': CL_SWAP_ROUTER,
  'aero-cl3': CL3_SWAP_ROUTER,
  'uni-v3': UNI_SWAP_ROUTER02,
  'uni-v2': UNI_V2_ROUTER
}

/** One answer per (router, venue) per session; an allowlist changes by a transaction. */
const targetCache = new Map<string, boolean>()

export function clearRouterTargetCache(): void {
  targetCache.clear()
}

/**
 * Which of these venues the platform router will actually call.
 *
 * A venue the router refuses is not a cheaper route, it is a transaction that
 * reverts with `TargetNotAllowed` after the wallet has already opened — so the
 * page drops it at ranking time, the same way it drops a KyberSwap row the
 * router would refuse. The check exists because it has a live case: Slipstream
 * factory 3 carries the tokenized stocks and was **not** on the allowlist when
 * the venue was added (scope 40 §1.7), so the page has to be correct about a
 * venue it can see and cannot use.
 *
 * A read that fails is reported as allowed: the dry run before the wallet
 * prompt is the gate that refuses, and hiding every route because one `isTarget`
 * call timed out would be a worse answer than showing one that may not send.
 */
export async function readAllowedVenues(
  provider: ethers.providers.Provider,
  routerAddress: string,
  venues: Iterable<OnChainVenue>
): Promise<Set<OnChainVenue>> {
  const wanted = [...new Set(venues)]
  const allowed = new Set<OnChainVenue>()
  await Promise.all(
    wanted.map(async (venue) => {
      const key = `${routerAddress.toLowerCase()}:${venue}`
      const cached = targetCache.get(key)
      if (cached !== undefined) {
        if (cached) allowed.add(venue)
        return
      }
      let ok = true
      try {
        const router = new ethers.Contract(routerAddress, SWAP_ROUTER_IFACE, provider)
        ok = Boolean(await router.isTarget(VENUE_TARGET[venue]))
        targetCache.set(key, ok)
      } catch {
        ok = true
      }
      if (ok) allowed.add(venue)
    })
  )
  return allowed
}

/**
 * Wraps a venue transaction into `router.swap(...)`.
 *
 * The adapter built `venueTx` for msg.sender = router (the recipient is the
 * account, explicitly, in every adapter) and for the NET amount, which is what
 * `quote.amountIn` carries. The router receives the GROSS amount — as value
 * for ETH, by transferFrom for a token — and takes the fee itself.
 */
export function wrapForRouter(
  venueTx: ethers.PopulatedTransaction,
  args: ExecuteArgs,
  routerAddress: string
): ethers.PopulatedTransaction {
  const gross = args.quote.grossAmountIn ?? args.quote.amountIn
  if (!venueTx.to) throw new Error('wrapForRouter: the venue transaction has no target')
  const params = {
    tokenIn: args.tokenIn.isNative ? NATIVE_SENTINEL : args.tokenIn.address,
    tokenOut: args.tokenOut.isNative ? NATIVE_SENTINEL : args.tokenOut.address,
    amountIn: ethers.BigNumber.from(gross.toString()),
    minAmountOut: ethers.BigNumber.from(args.minOut.toString()),
    to: args.account,
    target: venueTx.to,
    data: venueTx.data ?? '0x',
    deadline: ethers.BigNumber.from(args.deadline.toString())
  }
  return {
    to: routerAddress,
    data: SWAP_ROUTER_IFACE.encodeFunctionData('swap', [params]),
    value: args.tokenIn.isNative ? ethers.BigNumber.from(gross.toString()) : ethers.constants.Zero
  }
}

/**
 * A gas limit for a router swap: the estimate plus a third.
 *
 * Measured on the fork rehearsal (2026-09-05): a node's estimate for
 * router → KyberSwap executor → pools came in low and the transaction
 * reverted on send while its dry run had passed. The margin is cheap on Base
 * and the unused part is refunded.
 */
export function withGasMargin(estimate: ethers.BigNumber): ethers.BigNumber {
  return estimate.mul(13).div(10)
}

/**
 * The router's own custom errors, by name, from whatever shape ethers hands
 * back — so `decodeSwapError` gets a word it can match instead of a hex blob.
 * Returns null when the revert is not one of the router's (a venue's, say).
 */
export function nameRouterError(err: unknown): string | null {
  const data = revertDataOf(err)
  if (!data || data === '0x' || data.length < 10) return null
  try {
    const parsed = SWAP_ROUTER_IFACE.parseError(data)
    return parsed?.name ?? null
  } catch {
    return null
  }
}

function revertDataOf(err: unknown): string | null {
  const seen = new Set<unknown>()
  const visit = (node: unknown, depth: number): string | null => {
    if (!node || typeof node !== 'object' || depth > 5 || seen.has(node)) return null
    seen.add(node)
    const record = node as Record<string, unknown>
    if (typeof record.data === 'string' && record.data.startsWith('0x') && record.data.length >= 10) {
      return record.data
    }
    for (const key of ['data', 'error', 'info', 'cause', 'reason']) {
      const found = visit(record[key], depth + 1)
      if (found) return found
    }
    return null
  }
  return visit(err, 0)
}
